/**
 * Meterware — fabric sold by fractional length.
 *
 * The cart can only hold integer quantities, so meterware variants are priced
 * per base unit (e.g. 0,1 m). Cart quantity 15 therefore means 1,5 m. This file
 * turns that integer into a metre-based interface: a length field, preset
 * pills, and a headline price showing the total for the chosen length.
 *
 * A variant is meterware when unit_price_measurement.quantity_value < 1. That
 * value is 1.0 on whole-metre products, so field presence alone is not a test.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE DEFINES ITS OWN ELEMENT INSTEAD OF EDITING THE THEME'S JS
 *
 * The behaviour needs to change how the quantity control and ProductInfo
 * behave, both of which ship with the theme and are replaced wholesale on a
 * theme update. Editing them in place means this is silently lost next time
 * Shopify ships a Trade release.
 *
 * So nothing here touches global.js or product-info.js:
 *
 *  - <meterware-input> is its own custom element, rendered by the template in
 *    place of <quantity-input> for meterware variants only. It cannot reuse
 *    QuantityInput by patching the prototype, because that class binds its
 *    listeners in the constructor (`this.onInputChange.bind(this)`), which has
 *    already run by the time this file loads. Those bound handlers keep
 *    pointing at the original methods, and the original button handler calls
 *    stepUp() — which throws on the text input meterware needs.
 *
 *  - ProductInfo.setQuantityBoundries IS patched on the prototype, which is
 *    sound because it is invoked as a normal method call rather than a
 *    pre-bound listener. Its first invocation happens before this file runs,
 *    so it is replayed once at init.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // --- Fehlermeldung an den Endpoint ---------------------------------------
  // Was beim Kunden schiefgeht, sieht sonst niemand: Der Kunde geht einfach.
  // Darum meldet dieses Skript Fehler kurz an den Endpoint (/client-error),
  // der ab drei Meldungen pro Stunde eine Mail schickt. Höchstens drei
  // Meldungen pro Seitenaufruf, keine Kundendaten — nur Fehler, Seite, Browser.
  let mwReports = 0;
  function mwEndpoint() {
    const el = document.querySelector('meterware-length-form[data-endpoint]');
    return el ? el.dataset.endpoint.replace(/\/$/, '') : '';
  }
  function mwReport(kind, error, extra) {
    try {
      if (mwReports >= 3) return;
      const endpoint = mwEndpoint();
      if (!endpoint || /127\.0\.0\.1|localhost/.test(endpoint)) return;
      mwReports++;
      const el = document.querySelector('meterware-length-form');
      const body = JSON.stringify(Object.assign({
        kind: kind,
        msg: error && error.message ? error.message : String(error || ''),
        stack: error && error.stack ? String(error.stack).slice(0, 600) : '',
        url: location.href.slice(0, 200),
        ua: navigator.userAgent.slice(0, 200),
        product: el ? el.dataset.productId : '',
      }, extra || {}));
      const target = endpoint + '/client-error';
      // text/plain: kein Preflight, kommt auch beim Verlassen der Seite noch an.
      if (navigator.sendBeacon) navigator.sendBeacon(target, new Blob([body], { type: 'text/plain' }));
      else fetch(target, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true });
    } catch (e) { /* Melden darf nie selbst stören */ }
  }
  window.addEventListener('error', function (e) {
    if (e.filename && /meterware/.test(e.filename)) mwReport('script', e.error || e.message);
  });
  window.addEventListener('unhandledrejection', function (e) {
    const r = e.reason;
    if (r && r.stack && /meterware/.test(String(r.stack))) mwReport('promise', r);
  });

  const MW_LENGTH_PROPERTY = 'L\u00e4nge';

  /**
   * Minimal money formatter driven by the shop's own money_format, so a total
   * computed here matches one rendered by Liquid's `money` filter.
   */
  const mwMoney = {
    format(cents, moneyFormat) {
      const format = moneyFormat || '{{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      const token = match ? match[1] : 'amount';
      let value;

      switch (token) {
        case 'amount_no_decimals':
          value = this.number(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = this.number(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.number(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = this.number(cents, 2, "'", '.');
          break;
        default:
          value = this.number(cents, 2, ',', '.');
      }

      return format.replace(placeholder, value);
    },

    number(cents, decimals, thousands, decimal) {
      const parts = (cents / 100).toFixed(decimals).split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    },
  };

  /**
   * The length control. Mirrors the shape of the theme's QuantityInput (same
   * markup, same .quantity__button children) but owns its own listeners.
   */
  class MeterwareInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input.quantity__input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.input.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    connectedCallback() {
      this.setupMeterware();
    }

    onInputChange() {
      this.mwSync();
      this.validateQtyRules();
    }

    onButtonClick(event) {
      this.mwOnButtonClick(event);
    }

    validateQtyRules() {
      this.mwValidateQtyRules();
    }

    /**
     * Switch the visible field to metres and post an integer through a hidden
     * field. Safe to call repeatedly; it returns early once set up.
     *
     * The field becomes type="text", not type="number": a number input
     * silently discards "2,3" and German customers type the comma.
     */
    setupMeterware() {
      if (!this.input) return;
      const data = this.input.dataset;
      if (data.meterware !== 'true' || this.mwHidden) return;

      this.mwPerUnit = parseInt(data.mwPerUnit, 10);
      if (!this.mwPerUnit || this.mwPerUnit < 2) return;

      this.mwStep = parseFloat(data.mwStep) || 1 / this.mwPerUnit;
      this.mwMin = parseFloat(data.mwMin) || this.mwStep;
      this.mwDefault = Math.max(parseFloat(data.mwDefault) || this.mwMin, this.mwMin);
      this.mwDecimals = (String(this.mwStep).split('.')[1] || '').length;
      this.mwSeparator = (document.documentElement.lang || '').toLowerCase().startsWith('de') ? ',' : '.';

      const qty = parseInt(this.input.value, 10) || Math.round(this.mwMin * this.mwPerUnit);

      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = this.input.getAttribute('name');
      const formId = this.input.getAttribute('form');
      if (formId) hidden.setAttribute('form', formId);
      hidden.value = qty;
      this.input.removeAttribute('name');
      this.input.after(hidden);
      this.mwHidden = hidden;

      // Checkout cannot be customised on this plan, so it will show
      // "15 x EUR0,59". A line item property carries the human-readable length
      // through to checkout, the order record and the confirmation email.
      const lengthProperty = document.createElement('input');
      lengthProperty.type = 'hidden';
      lengthProperty.name = 'properties[' + MW_LENGTH_PROPERTY + ']';
      if (formId) lengthProperty.setAttribute('form', formId);
      hidden.after(lengthProperty);
      this.mwLengthProperty = lengthProperty;

      // Metre bounds are kept on the instance, never in data-min / step /
      // max. The theme reads those with parseInt, so "0.5" would become 0 and
      // reset the field — and script order decides who reads them first.
      const max = parseFloat(this.input.max);
      this.mwMax = isNaN(max) ? null : max / this.mwPerUnit;

      this.input.type = 'text';
      this.input.setAttribute('inputmode', 'decimal');
      this.input.setAttribute('autocomplete', 'off');
      this.input.removeAttribute('min');
      this.input.removeAttribute('max');
      this.input.value = this.mwFormat(qty / this.mwPerUnit);

      this.mwBindPresets();
      this.mwSync();
    }

    mwParse(value) {
      return parseFloat(String(value).replace(',', '.').trim());
    }

    mwNormalize(metres) {
      return Number(metres.toFixed(this.mwDecimals));
    }

    mwFormat(metres) {
      return String(this.mwNormalize(metres)).replace('.', this.mwSeparator);
    }

    mwSync() {
      if (!this.mwHidden) return;

      let metres = this.mwParse(this.input.value);
      if (isNaN(metres)) metres = this.mwMin;

      // Snap to the nearest step, then clamp. Avoids 0.30000000000000004.
      metres = Math.max(1, Math.round(metres / this.mwStep)) * this.mwStep;
      if (metres < this.mwMin) metres = this.mwMin;
      if (this.mwMax != null && metres > this.mwMax) metres = this.mwMax;

      this.input.value = this.mwFormat(metres);
      this.mwHidden.value = Math.round(metres * this.mwPerUnit);
      if (this.mwLengthProperty) {
        this.mwLengthProperty.value = this.mwFormat(metres) + ' ' + (this.input.dataset.mwUnit || 'm');
      }
      this.mwUpdateTotal(Number(this.mwHidden.value));
      this.mwSyncPresets(metres);
      this.mwUpdateAvailability();
    }

    /**
     * Stock can fall below the smallest cut we sell — 0,3 m left against a
     * 0,5 m minimum. The field would otherwise offer 0,3 m and the add would
     * be rejected at the cart. Disable instead, and never re-enable: the theme
     * owns the button's disabled state for sold-out variants.
     */
    mwUpdateAvailability() {
      const unavailable = this.mwMax != null && this.mwMax < this.mwMin;
      this.classList.toggle('meterware-input--unavailable', unavailable);
      if (!unavailable) return;

      const form = this.mwHidden && this.mwHidden.form;
      if (!form) return;
      form.querySelectorAll('button[name="add"]').forEach((button) => {
        button.disabled = true;
      });
    }

    mwOnButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;
      const direction = event.target.name === 'plus' ? 1 : -1;
      const current = this.mwParse(this.input.value);

      this.input.value = this.mwFormat((isNaN(current) ? this.mwMin : current) + direction * this.mwStep);
      this.mwSync();

      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      this.validateQtyRules();
    }

    /**
     * Called by the patched setQuantityBoundries with boundaries in base units,
     * so the theme's integer quantity-rule maths never sees a fractional metre.
     */
    mwApplyBoundaries(minQty, maxQty) {
      this.setupMeterware();
      if (!this.mwHidden) return;

      this.mwMin = minQty / this.mwPerUnit;
      this.mwMax = maxQty ? maxQty / this.mwPerUnit : null;
      // Reset to the default length rather than the minimum.
      this.input.value = this.mwFormat(Math.max(this.mwDefault || this.mwMin, this.mwMin));
      this.mwSync();
    }

    mwValidateQtyRules() {
      const value = this.mwParse(this.input.value);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= this.mwMin);
      if (buttonPlus && this.mwMax != null) buttonPlus.classList.toggle('disabled', value >= this.mwMax);
    }

    /**
     * Length preset pills. They sit outside <quantity-input> and are not
     * form-associated, so they never post a value of their own; they just drive
     * the length field, which stays the single source of truth.
     */
    mwBindPresets() {
      const scope = this.closest('product-info') || document;
      const block = scope.querySelector('[data-meterware-block]');
      if (!block) return;

      this.mwPresets = Array.from(block.querySelectorAll('[data-meterware-preset]'));
      this.mwPresets.forEach((radio) => {
        if (radio.dataset.mwBound === 'true') return;
        radio.dataset.mwBound = 'true';
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          this.input.value = this.mwFormat(parseFloat(radio.value));
          this.input.dispatchEvent(this.changeEvent);
        });
      });
    }

    mwSyncPresets(metres) {
      if (!this.mwPresets) return;
      this.mwPresets.forEach((radio) => {
        radio.checked = Math.abs(parseFloat(radio.value) - metres) < 1e-9;
      });
    }

    /**
     * The headline price is the total for the selected length, so it is
     * recomputed whenever the length changes. The per-metre line underneath is
     * rendered by Liquid and stays put.
     */
    mwUpdateTotal(quantity) {
      const scope = this.closest('product-info') || document;
      const priceEl = scope.querySelector('.price[data-mw-price]');
      if (!priceEl) return;

      const format = priceEl.dataset.mwMoneyFormat;
      const unitCents = parseInt(priceEl.dataset.mwUnitCents, 10);
      if (isNaN(unitCents)) return;

      const total = mwMoney.format(unitCents * quantity, format);
      priceEl.querySelectorAll('.price__regular .price-item--regular').forEach((node) => {
        node.textContent = total;
      });
      priceEl.querySelectorAll('.price__sale .price-item--sale').forEach((node) => {
        node.textContent = total;
      });

      const compareCents = parseInt(priceEl.dataset.mwCompareCents, 10);
      if (!isNaN(compareCents)) {
        const compareTotal = mwMoney.format(compareCents * quantity, format);
        priceEl.querySelectorAll('.price__sale s.price-item--regular').forEach((node) => {
          node.textContent = compareTotal;
        });
      }
    }
  }

  if (!window.customElements.get('meterware-input')) {
    window.customElements.define('meterware-input', MeterwareInput);
  }

  function patchProductInfo() {
    const ProductInfo = window.customElements.get('product-info');
    if (!ProductInfo) return false;

    const proto = ProductInfo.prototype;
    if (proto.mwPatched) return true;
    proto.mwPatched = true;

    /**
     * The theme reads the quantity rules with parseInt, so a min of "0.5" and a
     * step of "0.1" both become 0 and the field is reset to 0. For meterware
     * the integer base-unit equivalents are read instead, and the result is
     * handed back to the quantity input to convert into metres.
     */
    const originalBoundries = proto.setQuantityBoundries;
    proto.setQuantityBoundries = function () {
      const input = this.quantityInput;
      if (!input || input.dataset.meterware !== 'true') {
        return originalBoundries.call(this);
      }

      const cartQuantity = input.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity, 10) : 0;
      const step = parseInt(input.dataset.mwStepQty, 10) || 1;
      const dataMin = parseInt(input.dataset.mwMinQty, 10) || 1;
      const dataMax = input.dataset.max ? parseInt(input.dataset.max, 10) : null;

      // Deliberately unlike the theme's rule, which drops the minimum to a
      // single step once the cart already holds the minimum. A 0,5 m minimum
      // cut applies to every order, so it must not decay to 0,1 m.
      let min = dataMin;
      const max = dataMax === null ? dataMax : dataMax - cartQuantity;
      if (max !== null && max > 0) min = Math.min(min, max);

      const quantityInputEl = input.closest('meterware-input, quantity-input');
      if (quantityInputEl && typeof quantityInputEl.mwApplyBoundaries === 'function') {
        quantityInputEl.mwApplyBoundaries(min, max);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }
    };

    return true;
  }

  function mwLineInfo(item) {
    const measurement = item.unit_price_measurement;
    if (!measurement || measurement.measured_type !== 'length') return null;
    const quantityValue = parseFloat(measurement.quantity_value);
    if (!(quantityValue > 0 && quantityValue < 1)) return null;

    const perUnit = Math.round(1 / quantityValue);
    const decimals = (String(quantityValue).split('.')[1] || '').length;
    const metres = item.quantity / perUnit;
    const separator = (document.documentElement.lang || '').toLowerCase().startsWith('de') ? ',' : '.';
    const label = String(Number(metres.toFixed(decimals))).replace('.', separator);

    return { perUnit, metres, label: label + ' ' + measurement.reference_unit };
  }

  /**
   * The length property is set when the item is added, but Shopify merges two
   * adds that carry identical properties — so adding 1,5 m twice yields one
   * line of 3,0 m still labelled "1,5 m". Quantity is the single source of
   * truth, so after every cart change any stale label is rewritten from it.
   */
  let mwSyncing = false;
  async function mwSyncCartLengths() {
    if (mwSyncing) return;
    mwSyncing = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const stale = [];

      cart.items.forEach((item) => {
        const info = mwLineInfo(item);
        if (!info) return;
        const current = (item.properties || {})[MW_LENGTH_PROPERTY];
        if (current !== info.label) {
          stale.push({
            id: item.key,
            quantity: item.quantity,
            properties: Object.assign({}, item.properties, { [MW_LENGTH_PROPERTY]: info.label }),
          });
        }
      });

      for (const change of stale) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(change),
        });
      }
      return stale.length;
    } catch (error) {
      // A failed reconciliation must never block checkout; the label is
      // cosmetic and the charged amount comes from the quantity.
      return 0;
    } finally {
      mwSyncing = false;
    }
  }

  /**
   * Cart lines. The theme's CartItems binds a delegated change listener and
   * reads event.target.value, so the field it sees must keep holding the
   * integer base-unit quantity — otherwise typing "3" for 3 m would set the
   * quantity to 3, i.e. 0,3 m.
   *
   * The theme's input is therefore left completely intact (name, data
   * attributes, its own +/- handlers) and merely hidden, with a metre-facing
   * field placed in front of it that writes through to it.
   */
  const MW_CART_SELECTOR =
    'cart-items .quantity__input[data-meterware="true"], ' +
    'cart-drawer-items .quantity__input[data-meterware="true"]';

  function mwEnhanceCartLine(source) {
    if (source.dataset.mwCartReady === 'true') return;
    // <meterware-input> already presents metres on product pages; enhancing it
    // again would stack a second field and a second unit label on top.
    if (source.closest('meterware-input')) return;
    if (!source.closest('cart-items, cart-drawer-items')) return;
    const perUnit = parseInt(source.dataset.mwPerUnit, 10);
    if (!perUnit || perUnit < 2) return;
    source.dataset.mwCartReady = 'true';

    const step = parseFloat(source.dataset.mwStep) || 1 / perUnit;
    const decimals = (String(step).split('.')[1] || '').length;
    const separator = (document.documentElement.lang || '').toLowerCase().startsWith('de') ? ',' : '.';
    const unit = source.dataset.mwUnit || 'm';

    const toMetres = (quantity) => String(Number((quantity / perUnit).toFixed(decimals))).replace('.', separator);
    const parse = (value) => parseFloat(String(value).replace(',', '.').trim());

    // The theme's .quantity control is a fixed width sized for the buttons plus
    // a plain integer. A unit label and a value like "1,2" do not fit, so the
    // wrapper is marked to size to its content instead.
    const wrapper = source.closest('.quantity');
    if (wrapper) wrapper.classList.add('quantity--meterware');

    source.classList.add('meterware-source-input');
    source.setAttribute('tabindex', '-1');
    source.setAttribute('aria-hidden', 'true');

    const display = document.createElement('input');
    display.type = 'text';
    display.className = source.className.replace('meterware-source-input', '').trim();
    display.setAttribute('inputmode', 'decimal');
    display.setAttribute('autocomplete', 'off');
    display.setAttribute('aria-label', source.getAttribute('aria-label') || unit);
    display.value = toMetres(parseInt(source.value, 10) || 0);
    source.after(display);

    const unitLabel = document.createElement('span');
    unitLabel.className = 'meterware-unit meterware-unit--cart';
    unitLabel.setAttribute('aria-hidden', 'true');
    unitLabel.textContent = unit;
    display.after(unitLabel);

    display.addEventListener('change', (event) => {
      // Keep the metre value away from the theme's delegated cart listener.
      event.stopPropagation();

      let metres = parse(display.value);
      if (isNaN(metres) || metres < 0) metres = 0;
      let quantity = Math.round(metres / step) * Math.round(step * perUnit);
      if (metres > 0 && quantity < 1) quantity = 1;

      display.value = toMetres(quantity);
      if (parseInt(source.value, 10) !== quantity) {
        source.value = quantity;
        source.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // The theme's own +/- buttons still drive the integer field directly.
    source.addEventListener('change', () => {
      display.value = toMetres(parseInt(source.value, 10) || 0);
    });
  }

  function mwEnhanceCartLines(root) {
    (root || document).querySelectorAll(MW_CART_SELECTOR).forEach(mwEnhanceCartLine);
  }

  /**
   * Plan B length control (one variant per length).
   *
   * Same control as plan A: preset pills above, one length field with -/+
   * below. The field is the single source of truth. Add to cart resolves the
   * field's length to a variant — an existing one is used, a missing one is
   * created by the meterware endpoint at that moment — then the theme's own
   * product form submits as usual with quantity 1.
   *
   * Pills are the curated presets only. A length created for one customer is
   * never shown as a pill, so it stays invisible to everyone else.
   */
  class MeterwareLengthForm extends HTMLElement {
    connectedCallback() {
      // The Liquid list ends with a null sentinel (trailing-comma workaround) — drop it.
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent).filter(Boolean);
      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.status = this.querySelector('.meterware-b__status');
      this.input = this.querySelector('.meterware-b__input');
      this.field = this.querySelector('[data-mw-field]');
      this.freeButton = this.querySelector('[data-mw-free]');
      this.min = parseFloat(this.dataset.min) || 0.5;
      this.step = parseFloat(this.dataset.step) || 0.1;
      this.max = parseFloat(this.dataset.max) || 10;
      // Shared roll: the most anyone can order is what is left on it.
      const unitM = parseFloat(this.dataset.unitM) || 0.1;
      // Ohne gepflegten Bestand gibt es keine Rolle, die begrenzen könnte — dann
      // gilt nur die Obergrenze aus den Einstellungen, wie vor der Umstellung.
      this.unlimited = this.dataset.unlimited === 'true';
      this.stockM = this.unlimited
        ? this.max
        : Number(((parseInt(this.dataset.stockUnits, 10) || 0) * unitM).toFixed(1));
      if (this.stockM < this.max) this.max = this.stockM;
      this.ppm = parseFloat(this.dataset.pricePerM) || 0;
      this.scope = this.closest('product-info') || document;
      this.resolving = null;
      // Only worth explaining the greyed-out express buttons if the shop shows any.
      this.hasExpress = !!(this.form && this.form.querySelector('.shopify-payment-button'));

      // Quantity is always one cut; hide the theme's quantity control and its
      // label. A class, not [hidden]: the theme's display:flex outranks it.
      this.scope.querySelectorAll('quantity-input, .quantity__label').forEach((el) => { el.classList.add('meterware-b__hidden'); });
      this.scope.querySelectorAll('variant-selects, variant-radios').forEach((el) => { el.hidden = true; });

      this.querySelectorAll('[data-mw-preset]').forEach((radio) =>
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          this.touched = true;
          this.setLength(parseFloat(radio.value));
        })
      );
      // "Free entry" swaps itself for the input, so the field is only on screen
      // when someone actually wants a length that is not a preset.
      this.defaultM = parseFloat(this.dataset.default) || this.min;
      this.touched = false;
      const touch = () => { this.touched = true; };
      if (this.freeButton) {
        this.freeButton.addEventListener('click', () => {
          touch();
          this.showField(true);
          this.input.focus();
          this.input.select();
        });
      }
      this.input.addEventListener('focus', touch);
      this.input.addEventListener('keydown', touch);
      // Browsers restore what was typed here before a reload and fire "change"
      // for it. Without a real interaction that is not the customer's choice,
      // so fall back to the default length instead of reviving an old value.
      this.input.addEventListener('change', () => {
        this.setLength(this.touched ? this.parseLen(this.input.value) : this.defaultM);
      });
      // While the customer is still typing, follow along: show the price for
      // what is in the field and start preparing that length right away. This
      // is what makes a new length feel instant — Shopify needs a few seconds
      // before a fresh variant can be sold, and those seconds now pass while
      // the customer reads the price instead of after the click.
      this.input.addEventListener('input', () => {
        if (!this.touched) return;
        const raw = this.input.value.trim();
        if (!/^\d+([.,]\d*)?$/.test(raw)) return;   // half-typed or nonsense: wait
        this.setLength(this.parseLen(raw), { typing: true });
      });
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.setLength(this.parseLen(this.input.value)); } });

      // The theme registers its own submit handler on this form before this
      // element connects, and at the target node listeners run in registration
      // order — so intercepting "submit" would be too late and the theme would
      // show its error for a variant that does not exist yet. The click on the
      // button happens earlier, so that is where an unresolved length is caught.
      if (this.form) {
        this.form.querySelectorAll('[name="add"], [type="submit"]').forEach((button) =>
          button.addEventListener('click', (e) => this.onSubmit(e), true)
        );
        this.form.addEventListener('submit', (e) => this.onSubmit(e), true);
        // Enter in the length field would submit the form the same way.
        this.input.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          this.setLength(this.parseLen(this.input.value));
        });
      }

      this.renderStock();
      this.setLength(this.defaultM);
    }

    renderStock() {
      const note = this.querySelector('.meterware-b__stock');
      const low = parseFloat(this.dataset.lowStockM) || 10;
      const pills = this.querySelectorAll('[data-mw-preset]');
      const form = this.form;
      pills.forEach((r) => { r.disabled = parseFloat(r.value) > this.stockM; });
      if (this.stockM < this.min) {
        note.className = 'meterware-b__note meterware-b__stock meterware-b__stock--out';
        note.textContent = this.dataset.noteOut;
        if (form) form.querySelectorAll('[type="submit"]').forEach((b) => { b.disabled = true; });
      } else if (this.stockM < low) {
        note.className = 'meterware-b__note meterware-b__stock meterware-b__stock--low';
        note.textContent = this.dataset.noteLow.replace('__LEN__', this.fmt(this.stockM) + ' m');
      } else {
        note.className = 'meterware-b__note meterware-b__stock meterware-b__stock--in';
        note.textContent = this.dataset.noteIn;
      }
    }

    // Debounced so that "12,5" typed at normal speed does not also create 12 m
    // on the way. Every length created here stays on the product.
    prefetch(m) {
      if (!this.touched) return;                 // not a length anyone chose yet
      clearTimeout(this.prefetchTimer);
      this.prefetchTimer = setTimeout(() => {
        if (this.resolving) return;              // an add is already running
        if (Math.abs(this.length - m) > 1e-9) return;   // moved on since
        this.ensureVariant(m)
          .then(() => { if (Math.abs(this.length - m) < 1e-9) this.setLength(m, { typing: document.activeElement === this.input }); })
          .catch(() => { /* the add-to-cart path reports problems */ });
      }, 700);
    }

    // By the time this runs the product JSON has confirmed the variant, so the
    // first add normally succeeds. If the cart is still a step behind it answers
    // "sold out" and throttles quick retries of that same variant — hence few
    // attempts with long gaps. Adding through the theme's own form would show
    // its "sold out" message on every attempt, so the add is done here and only
    // the finished cart is handed to the theme.
    // Holt die Warenkorb-Abschnitte noch einmal, solange die Zeile den erwarteten
    // Längennamen nicht trägt. Höchstens gut eine Sekunde, dann wird angezeigt,
    // was da ist — lieber ein seltener Schönheitsfehler als eine hängende Seite.
    //
    // Geholt wird über /cart/update.js mit leerem `updates`: ein Nichts-Änderer,
    // der den frischen Warenkorb UND die gerenderten Abschnitte zurückgibt. Der
    // naheliegende Weg /cart.js?sections=… kann das nicht — dort wird der
    // Parameter stillschweigend ignoriert (nachgemessen am Shop: die Antwort hat
    // überhaupt kein "sections"). renderContents greift auf
    // parsedState.sections[…] zu, wäre an so einer Antwort also mit einem
    // TypeError gescheitert: die Länge lag im Warenkorb, die Schublade blieb zu,
    // und der Kunde las „Diese Länge ist gerade nicht möglich" und legte erneut
    // ab. Genau im seltenen Fall, für den diese Funktion überhaupt da ist.
    async freshCart(data, title, cart, canRender) {
      if (!title || !canRender) return data;
      const passt = (d) => (d.items || []).some((i) => typeof i.title === 'string' && i.title.endsWith(title));
      if (passt(data)) return data;
      const sections = cart.getSectionsToRender().map((x) => x.id);
      for (let i = 0; i < 5; i++) {
        await new Promise((r) => setTimeout(r, 250));
        try {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: {}, sections, sections_url: window.location.pathname }),
          });
          if (!res.ok) continue;
          const frisch = await res.json();
          // Ohne Abschnitte ist die Antwort für renderContents unbrauchbar —
          // dann lieber bei der Antwort von /cart/add.js bleiben.
          if (!frisch || !frisch.sections) continue;
          // Überlagern, nicht ersetzen: Felder, die nur die Add-Antwort trägt
          // (`key` für cart-notification, `id` für cart-drawer), bleiben erhalten.
          const zusammen = { ...data, ...frisch };
          if (passt(zusammen)) return zusammen;
          data = zusammen;
        } catch (e) { /* weiter versuchen */ }
      }
      // Nach 1,25 s noch immer der alte Name. Der Warenkorb selbst stimmt
      // Sekunden später (am 26.09.2026 gemessen: „Reserve 7" -> „17,3 m", gleiche
      // Varianten-ID), aber die Schublade wurde bisher genau einmal gezeichnet —
      // mit „Reserve 7" — und nie wieder. Deshalb: im Hintergrund weiter
      // nachsehen und neu zeichnen, sobald der Name stimmt.
      this.nachzeichnen(title, cart, sections, data);
      return data;
    }

    async nachzeichnen(title, cart, sections, basis) {
      const passt = (d) => (d.items || []).some((i) => typeof i.title === 'string' && i.title.endsWith(title));
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        try {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: {}, sections, sections_url: window.location.pathname }),
          });
          if (!res.ok) continue;
          const frisch = await res.json();
          if (!frisch || !frisch.sections || !passt(frisch)) continue;
          cart.renderContents({ ...basis, ...frisch });
          return true;
        } catch (e) { /* weiter */ }
      }
      mwReport('cart-stale-title', title);
      return false;
    }

    // Bis der Warenkorb nachgezogen hat, darf der Kunde „Reserve 7" nicht lesen:
    // im gezeichneten Warenkorb den Platzhalter durch die bestellte Länge ersetzen.
    // Nur die Anzeige — die Bestellung trägt ohnehin dieselbe Variante.
    platzhalterUeberdecken(cart, title) {
      if (!cart || !title) return;
      const walker = document.createTreeWalker(cart, NodeFilter.SHOW_TEXT);
      const treffer = [];
      while (walker.nextNode()) if (/\bReserve \d+\b/.test(walker.currentNode.nodeValue)) treffer.push(walker.currentNode);
      treffer.forEach((n) => { n.nodeValue = n.nodeValue.replace(/\bReserve \d+\b/g, title); });
    }

    async addWithRetry(id, title, attempts = 4) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const canRender = cart && typeof cart.renderContents === 'function' && typeof cart.getSectionsToRender === 'function';
      const body = { items: [{ id: Number(id), quantity: 1 }] };
      if (canRender) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      this.throttled = false;
      for (let i = 0; i < attempts; i++) {
        let wait = 5000;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          if (response.ok) {
            let data = await response.json();
            // Eine eben umbenannte Reservevariante kann in der Antwort des
            // Warenkorbs noch ihren alten Namen tragen ("Reserve 1" statt
            // "10,1 m"), obwohl die Länge selbst schon stimmt. Der Warenkorb
            // selbst ist eine Sekunde später korrekt — also nicht falsch
            // anzeigen, sondern kurz warten und neu holen.
            data = await this.freshCart(data, title, cart, canRender);
            if (canRender) {
              if (cart.classList) cart.classList.remove('is-empty');
              cart.renderContents(data);
              // renderContents kann das Markup einen Takt später tauschen — zweimal decken.
              this.platzhalterUeberdecken(cart, title);
              setTimeout(() => this.platzhalterUeberdecken(cart, title), 300);
            } else {
              this.form.requestSubmit();   // theme markup unknown: let it do the add
            }
            return true;
          }
          if (response.status === 429) { this.throttled = true; wait = 10000; }
        } catch (e) { /* offline for a moment, try again */ }
        if (i < attempts - 1) await new Promise((r) => setTimeout(r, wait));
      }
      return false;
    }

    showField(show) {
      if (!this.field || !this.freeButton) return;
      this.field.hidden = !show;
      this.freeButton.hidden = show;
    }

    parseLen(t) { const n = parseFloat(String(t).replace(',', '.').trim()); return isNaN(n) ? this.length || this.min : n; }
    fmt(m) { return String(Number(m.toFixed(1))).replace('.', ','); }
    title(m) { return this.fmt(m) + ' m'; }
    // Integer maths so the estimate here equals what the endpoint charges (13,685 -> 13,69).
    priceCents(m) { return Math.round(Math.round(this.ppm * 100) * Math.round(m * 10) / 10); }

    // typing: called from the input event — leave the field's text alone and
    // keep it open, otherwise typing "1," would snap to "1" and hide the field.
    setLength(m, { typing = false } = {}) {
      m = Math.round(m / this.step) * this.step;
      m = Number(Math.min(this.max, Math.max(this.min, m)).toFixed(1));
      this.length = m;
      if (!typing) this.input.value = this.fmt(m);
      let onPreset = false;
      this.querySelectorAll('[data-mw-preset]').forEach((r) => {
        const hit = Math.abs(parseFloat(r.value) - m) < 1e-9;
        r.checked = hit;
        if (hit) onPreset = true;
      });
      // A preset closes the free entry again; anything else keeps it open.
      if (!typing) this.showField(!onPreset);

      const existing = this.variants.find((v) => v.title === this.title(m));
      const cents = existing ? existing.price : this.priceCents(m);
      // Only a length the storefront already sells goes into the form. A freshly
      // created one stays out until confirmed, otherwise the theme would submit
      // it itself and show "sold out" while Shopify is still catching up.
      const ready = !!(existing && !existing.pending);
      this.setFormVariant(ready ? existing.id : '');
      // Shopify needs a moment before a brand-new variant can be put in the
      // cart. Start that clock while the customer is still looking at the page
      // instead of when they press the button.
      if (!existing) this.prefetch(m);
      this.showPrice(cents, m);
      // Shopify's express buttons read the variant out of the form, so while a
      // length is being prepared "Mit Shop kaufen" greys itself out. Usually a
      // reserve variant takes over within a second or two and nobody notices —
      // so hold the explanation back, and only say it if the wait is real.
      clearTimeout(this.hintTimer);
      this.status.textContent = '';
      if (!ready && this.hasExpress) {
        this.hintTimer = setTimeout(() => {
          if (!this.resolving && this.idInput && !this.idInput.value) {
            this.status.textContent = this.dataset.expressWait || '';
          }
        }, 2500);
      }
    }

    // The express buttons (Shop Pay, PayPal, Google Pay) sit in the product form
    // and read the variant from it. The Shop-Pay instalment banner has a second
    // form of its own, which would otherwise keep quoting the product's first
    // variant instead of the chosen length.
    setFormVariant(id) {
      const value = id ? String(id) : '';
      if (this.idInput) this.idInput.value = value;
      if (!value) return;
      const installment = document.querySelector('form.installment input[name="id"]');
      if (installment) installment.value = value;
    }

    showPrice(cents, m) {
      const money = mwMoney.format(cents, this.dataset.moneyFormat);
      this.scope.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((n) => { n.textContent = money; });
      // Grundpreis follows the selected cut: its price divided by its length.
      const metres = m || this.length;
      if (metres) {
        // Der Grundpreis ist der Preis der 1-m-Variante, nicht der gerundete
        // Zeilenpreis geteilt durch die Länge: 2,98 € / 0,5 m ergäbe 5,96 €/m.
        const master = this.variants.find((v) => v.title === this.title(1));
        const perM = mwMoney.format(master ? master.price : Math.round(cents / metres), this.dataset.moneyFormat);
        // The theme's unit-price span holds amount and unit together ("€5,95/m").
        this.scope.querySelectorAll('.unit-price .price-item').forEach((n) => { n.textContent = perM + '/m'; });
      }
    }

    async ensureVariant(m) {
      const existing = this.variants.find((v) => v.title === this.title(m));
      if (existing) return existing;
      // Hängt der Endpoint, darf der Kunde nicht mithängen: nach 8 s abbrechen.
      // Er sieht dann den Hinweis, eine andere Länge zu wählen — die Leiter
      // (0,5–10 m) braucht den Endpoint ohnehin nicht.
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 8000);
      let res, data;
      try {
        res = await fetch(this.dataset.endpoint.replace(/\/$/, '') + '/variant', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: this.dataset.productId, length: m }),
          signal: abort.signal,
        });
        data = await res.json();
      } finally { clearTimeout(timer); }
      if (!res.ok) throw new Error(data.error || String(res.status));
      // Eine neu angelegte Variante verkauft der Storefront erst nach Sekunden;
      // eine umbenannte Reserve verkauft er sofort, trägt aber für einen
      // Augenblick noch ihren alten Namen. Beides muss bestätigt werden, sonst
      // steht im Warenkorb "Reserve 1" statt "6,4 m" (gesehen).
      const v = { id: Number(data.variantId), title: data.title, price: Math.round(Number(data.price) * 100), available: true, pending: data.source !== 'existing' };
      this.variants.push(v);   // remembered for this session only, never rendered as a pill
      if (v.pending) this.confirm(v);
      return v;
    }

    // Shopify hands out a new variant to the Admin API a few seconds before the
    // storefront will sell it. The product JSON is the cheap way to watch for
    // that moment: it carries no cart throttle, while asking the cart too early
    // answers "sold out" and then throttles further tries for that variant.
    confirm(v) {
      if (!v.pending) return Promise.resolve(true);
      if (v.confirming) return v.confirming;
      const url = '/products/' + this.dataset.handle + '.js';
      v.confirming = (async () => {
        for (let i = 0; i < 45; i++) {
          try {
            const res = await fetch(url + '?_=' + Date.now(), { cache: 'no-store', headers: { Accept: 'application/json' } });
            const product = await res.json();
            const hit = (product.variants || []).find((x) => x.id === v.id);
            // Der Titel gehört zur Prüfung: bei einer Reserve ist die Variante
            // längst verkäuflich, nur der Name hinkt kurz nach.
            if (hit && hit.available && hit.title === v.title) {
              v.pending = false;
              // The customer may still be on this length: hand it to the form now.
              if (Math.abs(this.length - this.parseLen(v.title)) < 1e-9) this.setLength(this.length, { typing: document.activeElement === this.input });
              return true;
            }
          } catch (e) { /* network hiccup, keep watching */ }
          // Am Anfang dicht nachsehen: eine Reserve ist meist nach dem ersten
          // oder zweiten Blick da. Danach reicht der Sekundentakt.
          await new Promise((r) => setTimeout(r, i < 8 ? 200 : 1000));
        }
        return false;
      })();
      return v.confirming;
    }

    onSubmit(event) {
      if (this.idInput && this.idInput.value) return;   // resolved: let the theme submit
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.resolving) return;
      const m = this.length;
      const button = this.form.querySelector('[type="submit"]');
      const spinner = button && button.querySelector('.loading__spinner, .loading-overlay__spinner');
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
      }
      this.status.textContent = this.title(m) + ' wird vorbereitet …';
      this.resolving = this.ensureVariant(m)
        .then((v) => {
          // Into the form only once confirmed; otherwise a second click after a
          // failed add would go through the theme and show its "sold out".
          if (!v.pending) this.setFormVariant(v.id);
          this.showPrice(v.price, m);
          return v.id;
        })
        // Das Urteil von confirm() MUSS zaehlen. Vorher stand hier
        // `.then(() => this.addWithRetry(...))` — der Rueckgabewert fiel unter
        // den Tisch, und nach dem Aufgeben wurde trotzdem gelegt. Genau so kam
        // am 26.09.2026 eine Zeile „Laenge: Reserve 2" zu 755,25 EUR in einen
        // echten Warenkorb: Shopify hatte die umbenannte Reserve noch nicht
        // ausgeliefert, confirm() gab false zurueck, und niemand hat hingehoert.
        // Nicht bestaetigt heisst: nicht in den Warenkorb.
        .then((id) => {
          const v = this.variants.find((x) => x.id === id);
          if (!v) return false;
          return this.confirm(v).then((bestaetigt) => (bestaetigt ? this.addWithRetry(id, this.title(m)) : false));
        })
        .then((ok) => {
          if (ok) { this.status.textContent = ''; this.setFormVariant(this.variants.find((v) => v.title === this.title(m))?.id); }
          else if (this.throttled) { this.status.textContent = 'Der Warenkorb ist gerade überlastet – bitte in ein paar Minuten noch einmal versuchen.'; mwReport('cart-throttled', this.title(m)); }
          else { this.status.textContent = 'Diese Länge wird noch vorbereitet – bitte gleich noch einmal auf „In den Warenkorb legen“ tippen.'; mwReport('add-unconfirmed', this.title(m)); }
        })
        .catch((e) => {
          this.status.textContent = 'Diese Länge ist gerade nicht möglich – bitte eine andere wählen.';
          mwReport('length-failed', e, { length: this.title(m) });
        }).finally(() => {
        this.resolving = null;
        if (button) {
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      });
    }
  }

  if (!window.customElements.get('meterware-length-form')) {
    window.customElements.define('meterware-length-form', MeterwareLengthForm);
  }

  function mwPatchAndReplayProductInfo() {
    if (!patchProductInfo()) return;

    // If ProductInfo connected before this ran, its first setQuantityBoundries()
    // used the unpatched maths. Replay it once.
    document.querySelectorAll('product-info').forEach((element) => {
      element.quantityInput = element.querySelector('.quantity__input');
      if (typeof element.setQuantityBoundries === 'function') element.setQuantityBoundries();
    });
  }

  function init() {
    // This file now loads globally, so product-info.js may not have run yet
    // (cart pages never load it at all).
    if (window.customElements.get('product-info')) {
      mwPatchAndReplayProductInfo();
    } else if (window.customElements.whenDefined) {
      window.customElements.whenDefined('product-info').then(mwPatchAndReplayProductInfo);
    }

    // Stückzahl × Länge darf die Rolle nicht übersteigen.
    //
    // Die Produktseite blendet die Stückzahl aus („immer ein Zuschnitt") und
    // begrenzt die Länge an der Rolle — im Warenkorb stand der Zähler jedoch offen.
    // 16 m auf einer 16-m-Rolle, dann Stückzahl 2, und 32 m lagen im Warenkorb:
    // die Längenvarianten sind ungetrackt mit CONTINUE, Shopify prüft dort nichts,
    // und die Flow bucht anschließend 320 Zehntel ab — die Rolle rutscht ins Minus.
    //
    // `stoffeya-meterware-grenze.liquid` schreibt die Höchstzahl an das Mengenfeld;
    // das bremst die Plus-Schaltfläche. Wer die Zahl von Hand tippt, kommt daran
    // vorbei — deshalb hier der Riegel, der nach jeder Warenkorbänderung nachsieht
    // und stillschweigend zurücksetzt.
    async function mwClampCartQuantities(durchgang) {
      const felder = document.querySelectorAll('.quantity__input[data-mw-max]');
      const zuviel = [];
      felder.forEach((feld) => {
        const grenze = parseInt(feld.dataset.mwMax, 10);
        const wert = parseInt(feld.value, 10);
        const key = feld.dataset.quantityLineKey;
        if (key && grenze >= 0 && wert > grenze && !zuviel.some((x) => x.id === key)) {
          zuviel.push({ id: key, quantity: grenze });
        }
      });
      if (!zuviel.length) return 0;
      for (const aenderung of zuviel) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(aenderung),
          });
        } catch (e) { /* beim nächsten Mal wieder */ }
      }
      // Der Warenkorb muss die zurückgesetzte Zahl auch zeigen.
      const schublade = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (schublade && typeof schublade.renderContents === 'function' && typeof schublade.getSectionsToRender === 'function') {
        try {
          const antwort = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: {}, sections: schublade.getSectionsToRender().map((x) => x.id), sections_url: window.location.pathname }),
          });
          const daten = await antwort.json();
          if (daten && daten.sections) schublade.renderContents(daten);
          // Wird eine frühere Zeile gekürzt, ändert sich die Grenze der späteren
          // (die Rolle teilen sich alle Zeilen desselben Stoffes). Also noch
          // einmal nachsehen — begrenzt, damit daraus keine Schleife wird.
          if ((durchgang || 0) < 3) return mwClampCartQuantities((durchgang || 0) + 1);
        } catch (e) { /* Anzeige hinkt dann bis zum nächsten Laden nach */ }
      } else {
        window.location.reload();
      }
      return zuviel.length;
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, mwSyncCartLengths);
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => mwClampCartQuantities());
    }
    mwSyncCartLengths();
    mwClampCartQuantities();

    mwEnhanceCartLines(document);
    // Cart sections are replaced wholesale after every change, so re-enhance
    // whatever gets swapped in.
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.quantity__input[data-meterware="true"]')) mwEnhanceCartLine(node);
          mwEnhanceCartLines(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
